import { useState } from "react";
import client, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { AddHoursTarget } from "@/components/AddHoursTarget";

const todayUK = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });
const blank = () => ({ workerId: "", date: todayUK(), startTime: "09:00", endTime: "17:00", reason: "", target: "general", jobId: null });

export function AddHoursDialog({ users, t, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.workerId || !form.date || !form.startTime || !form.endTime) return toast.error(t("att.addMissing", "Pick a worker, date and times"));
    if (form.target === "job" && !form.jobId) return toast.error(t("att.pickJob", "Pick a job"));
    setBusy(true);
    try {
      await client.post("/attendance/manual", { ...form, siteId: null });
      toast.success(t("ts.hoursAdded", "Hours added")); setOpen(false); setForm(blank()); onAdded(form.date);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" data-testid="ts-add-shift" className="bg-brand hover:bg-brand-dark" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1.5" />{t("att.addShift", "Add shift")}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="ts-add-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("ts.addHours", "Add field hours")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("att.worker", "Worker")}</Label>
              <Select value={form.workerId} onValueChange={(v) => setForm({ ...form, workerId: v, jobId: null, target: "general" })}>
                <SelectTrigger data-testid="ts-add-worker" className="mt-1"><SelectValue placeholder={t("att.selectWorker", "Select worker")} /></SelectTrigger>
                <SelectContent>{users.filter((u) => ["cleaner", "supervisor", "office"].includes(u.role)).map((u) => <SelectItem key={u.id} value={u.id}>{u.displayName}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>{t("att.date", "Date")}</Label><Input type="date" data-testid="ts-add-date" className="mt-1" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value, jobId: null, target: form.target === "job" ? "general" : form.target })} /></div>
              <div><Label>{t("att.in", "In")}</Label><Input type="time" data-testid="ts-add-start" className="mt-1" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} /></div>
              <div><Label>{t("att.out", "Out")}</Label><Input type="time" data-testid="ts-add-end" className="mt-1" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} /></div>
            </div>
            <AddHoursTarget form={form} setForm={setForm} t={t} />
            <div><Label>{t("att.reason", "Reason / note")}</Label><Input data-testid="ts-add-reason" className="mt-1" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder={t("att.reasonPh", "e.g. forgot to clock in")} /></div>
          </div>
          <DialogFooter><Button data-testid="ts-add-save" onClick={save} disabled={busy} className="bg-brand hover:bg-brand-dark">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{t("common.save", "Save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
